import { useMemo, useState } from 'react';
import { Checkbox, Input, Tag, Empty, Tooltip } from 'antd';
import { SearchOutlined, InfoCircleOutlined } from '@ant-design/icons';
import {
    PERMISSION_ACTIONS,
    PERMISSION_GROUPS,
    PERMISSION_MODULES,
    buildPermKey,
    getModule,
    getActionDescription,
    type PermissionActionKey,
} from '../../../config/permissionModules';
import { getScreensForPermission } from '../../../config/routeTitles';
import { removeVietnameseTones } from '../../../lib/utils';

/**
 * Chuỗi tìm kiếm (không dấu) của mỗi module: tên, mô tả, tên quyền, mô tả quyền
 * và tên các màn hình quyền đó mở — gõ "luong" hay "phieu chi" đều ra đúng module.
 */
const MODULE_SEARCH_TEXT: Record<string, string> = Object.fromEntries(
    PERMISSION_MODULES.map((m) => [
        m.key,
        removeVietnameseTones(
            [
                m.key,
                m.label,
                m.description ?? '',
                ...m.actions.flatMap((a) => [
                    PERMISSION_ACTIONS[a]?.label ?? a,
                    getActionDescription(m.key, a),
                    ...getScreensForPermission(buildPermKey(m.key, a)),
                ]),
            ].join(' ')
        ),
    ])
);

interface Props {
    value: string[];
    onChange: (next: string[]) => void;
    /** Disable mọi tương tác (chế độ xem) */
    readOnly?: boolean;
}

/**
 * UI chọn quyền theo module.
 * - Lọc theo group + ô tìm kiếm (tên module, tên quyền, tên màn hình; gõ không dấu được).
 * - Lọc "Chỉ hiện module đã chọn quyền" để rà lại nhóm quyền lớn.
 * - Hàng đầu mỗi module có checkbox "Tất cả" để bật/tắt nhanh.
 * - Tooltip mỗi quyền ghi rõ quyền đó mở những màn hình nào.
 */
export default function PermissionPicker({ value, onChange, readOnly }: Props) {
    const [activeGroup, setActiveGroup] = useState<string>('all');
    const [keyword, setKeyword] = useState('');
    const [onlySelected, setOnlySelected] = useState(false);

    const selectedSet = useMemo(() => new Set(value), [value]);

    const visibleModuleKeys = useMemo(() => {
        const fromGroup =
            activeGroup === 'all'
                ? PERMISSION_MODULES.map((m) => m.key)
                : PERMISSION_GROUPS.find((g) => g.key === activeGroup)?.moduleKeys ?? [];

        const kw = removeVietnameseTones(keyword.trim());
        return fromGroup.filter((mk) => {
            const m = getModule(mk);
            if (!m) return false;
            if (kw && !MODULE_SEARCH_TEXT[mk]?.includes(kw)) return false;
            if (onlySelected && !m.actions.some((a) => selectedSet.has(buildPermKey(mk, a)))) return false;
            return true;
        });
    }, [activeGroup, keyword, onlySelected, selectedSet]);

    const update = (next: Set<string>) => {
        onChange(Array.from(next));
    };

    const togglePerm = (moduleKey: string, action: PermissionActionKey, checked: boolean) => {
        const key = buildPermKey(moduleKey, action);
        const next = new Set(selectedSet);
        if (checked) next.add(key);
        else next.delete(key);
        update(next);
    };

    const toggleModuleAll = (moduleKey: string, checked: boolean) => {
        const m = getModule(moduleKey);
        if (!m) return;
        const next = new Set(selectedSet);
        m.actions.forEach((a) => {
            const k = buildPermKey(moduleKey, a);
            if (checked) next.add(k);
            else next.delete(k);
        });
        update(next);
    };

    const toggleGroupAll = (groupKey: string, checked: boolean) => {
        const moduleKeys =
            groupKey === 'all'
                ? PERMISSION_MODULES.map((m) => m.key)
                : PERMISSION_GROUPS.find((g) => g.key === groupKey)?.moduleKeys ?? [];
        const next = new Set(selectedSet);
        moduleKeys.forEach((mk) => {
            const m = getModule(mk);
            if (!m) return;
            m.actions.forEach((a) => {
                const k = buildPermKey(mk, a);
                if (checked) next.add(k);
                else next.delete(k);
            });
        });
        update(next);
    };

    // Đếm số permission đã chọn / tổng cho mỗi group (cho tab badge)
    const groupStats = useMemo(() => {
        const stats: Record<string, { selected: number; total: number }> = {};
        let allSel = 0;
        let allTotal = 0;
        for (const g of PERMISSION_GROUPS) {
            let sel = 0;
            let total = 0;
            for (const mk of g.moduleKeys) {
                const m = getModule(mk);
                if (!m) continue;
                total += m.actions.length;
                for (const a of m.actions) {
                    if (selectedSet.has(buildPermKey(mk, a))) sel += 1;
                }
            }
            stats[g.key] = { selected: sel, total };
            allSel += sel;
            allTotal += total;
        }
        stats.all = { selected: allSel, total: allTotal };
        return stats;
    }, [selectedSet]);

    const currentGroupStats = groupStats[activeGroup] ?? { selected: 0, total: 0 };
    const currentGroupAllChecked =
        currentGroupStats.total > 0 && currentGroupStats.selected === currentGroupStats.total;
    const currentGroupIndeterminate =
        currentGroupStats.selected > 0 && currentGroupStats.selected < currentGroupStats.total;

    return (
        <div className="flex flex-col gap-3">
            {/* Toolbar: tổng số + search */}
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm">
                    Đã chọn{' '}
                    <span className="font-semibold text-[#3e91ab]">{groupStats.all.selected}</span>
                    {' / '}
                    <span className="font-semibold">{groupStats.all.total}</span> quyền
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    <Checkbox checked={onlySelected} onChange={(e) => setOnlySelected(e.target.checked)}>
                        Chỉ hiện module đã chọn
                    </Checkbox>
                    <Input
                        allowClear
                        prefix={<SearchOutlined />}
                        placeholder="Tìm module, quyền, màn hình..."
                        value={keyword}
                        onChange={(e) => setKeyword(e.target.value)}
                        className="w-full max-w-[280px]"
                    />
                </div>
            </div>

            {/* Tabs group */}
            <div className="flex flex-wrap gap-2">
                {[{ key: 'all', label: 'Tất cả' }, ...PERMISSION_GROUPS].map((g) => {
                    const s = groupStats[g.key] ?? { selected: 0, total: 0 };
                    const active = activeGroup === g.key;
                    return (
                        <button
                            key={g.key}
                            type="button"
                            onClick={() => setActiveGroup(g.key)}
                            className={`px-3 py-1 text-sm rounded border transition flex items-center gap-2 ${active
                                ? 'bg-[#3e91ab] text-white border-[#3e91ab]'
                                : 'bg-white border-gray-200 hover:border-[#3e91ab] text-gray-700'
                                }`}
                        >
                            <span>{g.label}</span>
                            <span
                                className={`text-[11px] px-1.5 rounded ${active ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-600'
                                    }`}
                            >
                                {s.selected}/{s.total}
                            </span>
                        </button>
                    );
                })}
            </div>

            {/* Chọn tất cả của group hiện tại */}
            <div className="border rounded px-3 py-2 bg-gray-50 flex items-center justify-between">
                <Checkbox
                    checked={currentGroupAllChecked}
                    indeterminate={currentGroupIndeterminate}
                    disabled={readOnly}
                    onChange={(e) => toggleGroupAll(activeGroup, e.target.checked)}
                >
                    <span className="font-medium">Chọn tất cả quyền trong nhóm này</span>
                </Checkbox>
            </div>

            {/* Danh sách module */}
            <div className="flex flex-col gap-2 max-h-[55vh] overflow-y-auto pr-1">
                {visibleModuleKeys.length === 0 && (
                    <Empty
                        description={onlySelected && !keyword ? 'Chưa chọn quyền nào trong nhóm này' : 'Không tìm thấy module'}
                        className="my-6"
                    />
                )}

                {visibleModuleKeys.map((mk) => {
                    const m = getModule(mk);
                    if (!m) return null;
                    const selectedActions = m.actions.filter((a) =>
                        selectedSet.has(buildPermKey(mk, a))
                    );
                    const allChecked = selectedActions.length === m.actions.length;
                    const indeterminate =
                        selectedActions.length > 0 && selectedActions.length < m.actions.length;

                    return (
                        <div
                            key={mk}
                            className="border rounded-md hover:border-[#3e91ab] transition bg-white"
                        >
                            <div className="flex items-center justify-between px-3 py-2 border-b bg-gray-50/60">
                                <Checkbox
                                    checked={allChecked}
                                    indeterminate={indeterminate}
                                    disabled={readOnly}
                                    onChange={(e) => toggleModuleAll(mk, e.target.checked)}
                                >
                                    <span className="font-medium text-gray-800">{m.label}</span>
                                    {m.description && (
                                        <Tooltip title={m.description}>
                                            <InfoCircleOutlined className="ml-1.5 text-gray-400 hover:text-[#3e91ab]" />
                                        </Tooltip>
                                    )}
                                </Checkbox>
                                <Tag color={allChecked ? 'cyan' : 'default'}>
                                    {selectedActions.length}/{m.actions.length}
                                </Tag>
                            </div>
                            <div className="px-3 py-2 flex flex-wrap gap-x-6 gap-y-2">
                                {m.actions.map((a) => {
                                    const action = PERMISSION_ACTIONS[a];
                                    const k = buildPermKey(mk, a);
                                    const screens = getScreensForPermission(k);
                                    const descText = getActionDescription(mk, a);
                                    const desc = descText || screens.length ? (
                                        <div>
                                            {descText && <div>{descText}</div>}
                                            {screens.length > 0 && (
                                                <div className={descText ? 'mt-1.5' : ''}>
                                                    <span className="opacity-75">Mở màn hình:</span> {screens.join(', ')}
                                                </div>
                                            )}
                                        </div>
                                    ) : null;
                                    const checkbox = (
                                        <Checkbox
                                            key={a}
                                            checked={selectedSet.has(k)}
                                            disabled={readOnly}
                                            onChange={(e) =>
                                                togglePerm(mk, a, e.target.checked)
                                            }
                                        >
                                            {action?.label ?? a}
                                        </Checkbox>
                                    );
                                    return desc ? (
                                        <Tooltip key={a} title={desc} placement="top">
                                            <span>{checkbox}</span>
                                        </Tooltip>
                                    ) : (
                                        checkbox
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
